import { BaseService } from './BaseService';
import { 
  USER_STATUSES, 
  USER_ROLES, 
  USER_SUBSCRIPTION_STATUSES,
  TIMESTAMP_FIELDS,
  AUDIT_FIELDS,
  COLLECTIONS
} from './types';
import { 
  isValidEmail, 
  validateRequiredFields, 
  createErrorResponse,
  createSuccessResponse,
  handleFirebaseError 
} from './utils';
import { doc, getDoc, collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
/**
 * UserService - Comprehensive user management operations
 * Extends BaseService for common CRUD operations
 * Provides user-specific functionality for admin operations
 */
export class UserService extends BaseService {
  constructor() {
    super(COLLECTIONS.USERS);
    this.plansCollection = COLLECTIONS.PRICING_PLANS;
  }

  // ===== USER CRUD OPERATIONS =====
  
  /**
   * Get all users with optional filtering and pagination
   * @param {Object} options - Query options (filters, pagination, sorting)
   * @returns {Promise<Object>} Success/error response with users data
   */
  async getAllUsers(options = {}) {
    try {
      const users = await this.getAll(options);
      return createSuccessResponse(users, 'Users fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch users', 'FETCH_ERROR', error);
    }
  }

  /**
   * Get a specific user by ID
   * @param {string} userId - User document ID
   * @returns {Promise<Object>} Success/error response with user data
   */
  async getUserById(userId) {
    try {
      const user = await this.getById(userId);
      return createSuccessResponse(user, 'User fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch user', 'FETCH_ERROR', error);
    }
  }

  /**
   * Create a new user with validation and default values
   * @param {Object} userData - User data to create
   * @param {string} adminUserId - ID of admin creating the user
   * @returns {Promise<Object>} Success/error response with created user
   */
  async createUser(userData, adminUserId = 'admin') {
    try {
      // Validation
      const requiredFields = ['name', 'email', 'role'];
      const validation = validateRequiredFields(userData, requiredFields);
      if (!validation.isValid) {
        return createErrorResponse(validation.message, 'VALIDATION_ERROR');
      }

      if (!isValidEmail(userData.email)) {
        return createErrorResponse('Invalid email format', 'VALIDATION_ERROR');
      }

      // Prepare user data with defaults
      const newUser = {
        uid: userData.email,
        name: userData.name,
        email: userData.email,
        role: userData.role || 'basic',
        customer_id: userData.customer_id || '',
        provider: 'admin-created',
        status: 'pending',
        subscriptions: [],
        permissions: this.getDefaultPermissions(),
        ...TIMESTAMP_FIELDS.CREATE(adminUserId),
        ...AUDIT_FIELDS.CREATE(adminUserId)
      };

      const result = await this.create(newUser, adminUserId);
      return createSuccessResponse(result, 'User created successfully');
    } catch (error) {
      return createErrorResponse('Failed to create user', 'CREATE_ERROR', error);
    }
  }

  /**
   * Update an existing user
   * @param {string} userId - User document ID
   * @param {Object} updateData - Data to update
   * @param {string} adminUserId - ID of admin updating the user
   * @returns {Promise<Object>} Success/error response with updated user
   */
  async updateUser(userId, updateData, adminUserId = 'admin') {
    try {
      // Validation
      if (!userId) {
        return createErrorResponse('User ID is required', 'VALIDATION_ERROR');
      }

      // Prepare update data with audit fields
      const updatePayload = {
        ...updateData,
        updatedAt: Timestamp.now(),
        lastModifiedBy: adminUserId
      };

      const result = await this.update(userId, updatePayload, adminUserId);
      return createSuccessResponse(result, 'User updated successfully');
    } catch (error) {
      return createErrorResponse('Failed to update user', 'UPDATE_ERROR', error);
    }
  }

  /**
   * Delete a user permanently
   * @param {string} userId - User document ID
   * @param {string} adminUserId - ID of admin deleting the user
   * @returns {Promise<Object>} Success/error response
   */
  async deleteUser(userId, adminUserId = 'admin') {
    try {
      if (!userId) {
        return createErrorResponse('User ID is required', 'VALIDATION_ERROR');
      }

      const result = await this.delete(userId);
      return createSuccessResponse(result, 'User deleted successfully');
    } catch (error) {
      return createErrorResponse('Failed to delete user', 'DELETE_ERROR', error);
    }
  }

  // ===== USER STATUS MANAGEMENT =====
  
  /**
   * Update user status with approval workflow
   * @param {string} userId - User document ID
   * @param {string} action - Action to perform ('approve', 'reject', 'suspend')
   * @param {string} notes - Approval notes
   * @param {string} reason - Rejection/suspension reason
   * @param {string} adminUserId - ID of admin performing the action
   * @returns {Promise<Object>} Success/error response with updated user
   */
  async updateUserStatus(userId, action, notes = '', reason = '', adminUserId = 'admin') {
    try {
      let newStatus;
      let updateData = {
        updatedAt: new Date(),
        lastModifiedBy: adminUserId,
        lastModifiedAt: new Date()
      };

      switch (action) {
        case 'approve':
          newStatus = 'approved';
          updateData = {
            ...updateData,
            status: newStatus,
            approvedBy: adminUserId,
            approvedAt: new Date(),
            approvalNotes: notes
          };
          break;
        case 'reject':
          newStatus = 'rejected';
          updateData = {
            ...updateData,
            status: newStatus,
            rejectedBy: adminUserId,
            rejectedAt: new Date(),
            rejectionReason: reason
          };
          break;
        case 'suspend':
          newStatus = 'suspended';
          updateData = {
            ...updateData,
            status: newStatus,
            suspendedBy: adminUserId,
            suspendedAt: new Date(),
            suspensionReason: reason
          };
          break;
        default:
          return createErrorResponse('Invalid action specified', 'VALIDATION_ERROR');
      }

      const result = await this.update(userId, updateData, adminUserId);
      return createSuccessResponse(result, `User ${action}ed successfully`);
    } catch (error) {
      return createErrorResponse(`Failed to ${action} user`, 'UPDATE_ERROR', error);
    }
  }

  /**
   * Approve a pending user
   * @param {string} userId - User document ID
   * @param {string} adminUserId - ID of admin approving the user
   * @param {string} notes - Optional approval notes
   * @returns {Promise<Object>} Success/error response
   */
  async approveUser(userId, adminUserId = 'admin', notes = '') {
    return this.updateUserStatus(userId, 'approve', notes);
  }

  /**
   * Reject a pending user
   * @param {string} userId - User document ID
   * @param {string} adminUserId - ID of admin rejecting the user
   * @param {string} reason - Reason for rejection
   * @returns {Promise<Object>} Success/error response
   */
  async rejectUser(userId, adminUserId = 'admin', reason = '') {
    return this.updateUserStatus(userId, 'reject', '', reason);
  }

  /**
   * Suspend an approved user
   * @param {string} userId - User document ID
   * @param {string} adminUserId - ID of admin suspending the user
   * @param {string} reason - Reason for suspension
   * @returns {Promise<Object>} Success/error response
   */
  async suspendUser(userId, adminUserId = 'admin', reason = '') {
    return this.updateUserStatus(userId, 'suspend', '', reason);
  }

  // ===== SUBSCRIPTION MANAGEMENT =====
  
  /**
   * Add subscription to user with simplified parameters
   * @param {string} userId - User document ID
   * @param {string} planId - Plan ID
   * @param {string} planName - Plan name
   * @param {string} startDate - Start date (ISO string)
   * @param {string} endDate - End date (ISO string)
   * @param {boolean} autoRenew - Auto-renew flag
   * @param {string} adminUserId - ID of admin adding the subscription
   * @returns {Promise<Object>} Success/error response with updated user
   */
  async addSubscription(userId, planId, planName, startDate, endDate, autoRenew = true, adminUserId = 'admin') {
    try {
      // Get the plan details
      const plan = await this.getPlanById(planId);
      if (!plan.success) {
        return createErrorResponse('Plan not found', 'NOT_FOUND');
      }

      const subscriptionData = {
        planId,
        planName,
        status: 'active',
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        autoRenew,
        purchasedAt: new Date(),
        maxStocksPerQuarter: plan.data.maxStocksPerQuarter || 0,
        riskLevel: plan.data.riskLevel || 1,
        features: plan.data.features || []
      };

      // Get user and plan data
      const userResult = await this.getUserById(userId);
      if (!userResult.success) {
        return userResult;
      }

      const user = userResult.data;
      const newSubscription = {
        ...subscriptionData,
        order: (user.subscriptions?.length || 0) + 1,
        createdAt: Timestamp.now(),
        createdBy: adminUserId
      };

      // Update user subscriptions
      const updatedSubscriptions = [...(user.subscriptions || []), newSubscription];
      const updatedPermissions = this.calculateUserPermissions(updatedSubscriptions);

      const updateData = {
        subscriptions: updatedSubscriptions,
        permissions: updatedPermissions,
        updatedAt: Timestamp.now(),
        lastModifiedBy: adminUserId
      };

      const result = await this.update(userId, updateData, adminUserId);
      return createSuccessResponse(result, 'Subscription added successfully');
    } catch (error) {
      console.log(error);
      return createErrorResponse('Failed to add subscription', 'SUBSCRIPTION_ERROR', error);
    }
  }

  /**
   * Remove a subscription from a user
   * @param {string} userId - User document ID
   * @param {string} subscriptionId - Subscription ID to remove
   * @param {string} adminUserId - ID of admin removing subscription
   * @returns {Promise<Object>} Success/error response
   */
  async removeSubscription(userId, subscriptionId, adminUserId = 'admin') {
    try {
      if (!userId || !subscriptionId) {
        return createErrorResponse('User ID and Subscription ID are required', 'VALIDATION_ERROR');
      }

      const userResult = await this.getUserById(userId);
      if (!userResult.success) {
        return userResult;
      }

      const user = userResult.data;
      const updatedSubscriptions = user.subscriptions.filter(sub => sub.id !== subscriptionId);
      const updatedPermissions = this.calculateUserPermissions(updatedSubscriptions);

      const updateData = {
        subscriptions: updatedSubscriptions,
        permissions: updatedPermissions,
        ...TIMESTAMP_FIELDS.UPDATE(adminUserId),
        ...AUDIT_FIELDS.UPDATE(adminUserId)
      };

      const result = await this.update(userId, updateData, adminUserId);
      return createSuccessResponse(result, 'Subscription removed successfully');
    } catch (error) {
      return createErrorResponse('Failed to remove subscription', 'SUBSCRIPTION_ERROR', error);
    }
  }

  /**
   * Update subscription status
   * @param {string} userId - User document ID
   * @param {string} subscriptionId - Subscription ID
   * @param {string} newStatus - New status
   * @param {string} adminUserId - ID of admin updating subscription
   * @returns {Promise<Object>} Success/error response
   */
  async updateSubscriptionStatus(userId, subscriptionId, newStatus, adminUserId = 'admin') {
    try {
      if (!userId || !subscriptionId || !newStatus) {
        return createErrorResponse('User ID, Subscription ID, and Status are required', 'VALIDATION_ERROR');
      }

      if (!Object.values(USER_SUBSCRIPTION_STATUSES).includes(newStatus)) {
        return createErrorResponse('Invalid subscription status', 'VALIDATION_ERROR');
      }

      const userResult = await this.getUserById(userId);
      if (!userResult.success) {
        return userResult;
      }

      const user = userResult.data;
      const updatedSubscriptions = user.subscriptions.map(sub => 
        sub.id === subscriptionId ? { ...sub, status: newStatus } : sub
      );
      const updatedPermissions = this.calculateUserPermissions(updatedSubscriptions);

      const updateData = {
        subscriptions: updatedSubscriptions,
        permissions: updatedPermissions,
        ...TIMESTAMP_FIELDS.UPDATE(adminUserId),
        ...AUDIT_FIELDS.UPDATE(adminUserId)
      };

      const result = await this.update(userId, updateData, adminUserId);
      return createSuccessResponse(result, 'Subscription status updated successfully');
    } catch (error) {
      return createErrorResponse('Failed to update subscription status', 'SUBSCRIPTION_UPDATE_ERROR', error);
    }
  }

  // ===== USER SEARCH AND FILTERING =====
  
  /**
   * Search users with query and filters
   * @param {string} query - Search query string
   * @param {Object} filters - Filter options (status, role, etc.)
   * @returns {Promise<Object>} Success/error response with filtered users
   */
  async searchUsers(query, filters = {}) {
    try {
      const users = await this.getAll();
      
      let filteredUsers = users.filter(user => {
        // Search query
        if (query) {
          const searchableFields = [
            user.name || '',
            user.email || '',
            user.customer_id || '',
            user.role || ''
          ];
          
          if (!searchableFields.some(field => 
            field.toLowerCase().includes(query.toLowerCase())
          )) {
            return false;
          }
        }

        // Status filter
        if (filters.status && filters.status !== 'all') {
          const userStatus = user.status || 'pending';
          if (userStatus !== filters.status) return false;
        }

        // Role filter
        if (filters.role) {
          if (user.role !== filters.role) return false;
        }

        // Subscription filter
        if (filters.hasActiveSubscription !== undefined) {
          const hasActive = user.subscriptions?.some(sub => sub.status === 'active');
          if (hasActive !== filters.hasActiveSubscription) return false;
        }

        return true;
      });

      return createSuccessResponse(filteredUsers, 'Users filtered successfully');
    } catch (error) {
      return createErrorResponse('Failed to search users', 'SEARCH_ERROR', error);
    }
  }

  /**
   * Get users by specific status
   * @param {string} status - User status to filter by
   * @returns {Promise<Object>} Success/error response with filtered users
   */
  async getUsersByStatus(status) {
    return this.searchUsers('', { status });
  }

  /**
   * Get users by specific role
   * @param {string} role - User role to filter by
   * @returns {Promise<Object>} Success/error response with filtered users
   */
  async getUsersByRole(role) {
    return this.searchUsers('', { role });
  }

  /**
   * Get users with active subscriptions
   * @returns {Promise<Object>} Success/error response with users having active subscriptions
   */
  async getUsersWithActiveSubscriptions() {
    return this.searchUsers('', { hasActiveSubscription: true });
  }

  // ===== STATISTICS AND ANALYTICS =====
  
  /**
   * Get total user count with optional filters
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Success/error response with count
   */
  async getUserCount(filters = {}) {
    try {
      const count = await this.getCount(filters);
      return createSuccessResponse(count, 'User count fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch user count', 'COUNT_ERROR', error);
    }
  }

  /**
   * Get users by specific subscription plan
   * @param {string} planId - Plan ID to filter by
   * @returns {Promise<Object>} Success/error response with users
   */
  async getUsersBySubscriptionPlan(planId) {
    try {
      const users = await this.getAll();
      const filteredUsers = users.filter(user => 
        user.subscriptions?.some(sub => sub.planId === planId && sub.status === 'active')
      );
      
      return createSuccessResponse(filteredUsers, 'Users by plan fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch users by plan', 'PLAN_FILTER_ERROR', error);
    }
  }

  /**
   * Get user statistics summary
   * @returns {Promise<Object>} Success/error response with statistics
   */
  async getUserStatistics() {
    try {
      const users = await this.getAll();
      
      const stats = {
        total: users.length,
        byStatus: {},
        byRole: {},
        withActiveSubscriptions: 0,
        recentlyCreated: 0,
        recentlyActive: 0
      };

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      users.forEach(user => {
        // Count by status
        const status = user.status || 'pending';
        stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;

        // Count by role
        const role = user.role || 'basic';
        stats.byRole[role] = (stats.byRole[role] || 0) + 1;

        // Count with active subscriptions
        if (user.subscriptions?.some(sub => sub.status === 'active')) {
          stats.withActiveSubscriptions++;
        }

        // Count recently created
        if (user.createdAt && user.createdAt.toDate() > thirtyDaysAgo) {
          stats.recentlyCreated++;
        }

        // Count recently active
        if (user.lastLoginAt && user.lastLoginAt.toDate() > thirtyDaysAgo) {
          stats.recentlyActive++;
        }
      });

      return createSuccessResponse(stats, 'User statistics fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch user statistics', 'STATS_ERROR', error);
    }
  }

  // ===== UTILITY METHODS =====
  
  /**
   * Get default permissions for new users
   * @returns {Object} Default permissions object
   */
  getDefaultPermissions() {
    return {
      canViewReports: false,
      canViewCharts: false,
      maxStocksPerQuarter: 0,
      riskLevelAccess: [],
      features: [],
      activePlans: []
    };
  }

  /**
   * Calculate user permissions based on active subscriptions
   * @param {Array} subscriptions - User's subscriptions
   * @returns {Object} Calculated permissions object
   */
  calculateUserPermissions(subscriptions) {
    const activeSubscriptions = subscriptions.filter(sub => sub.status === 'active');
    
    const maxStocksPerQuarter = activeSubscriptions.reduce((sum, sub) => 
      sum + (sub.maxStocksPerQuarter || 0), 0
    );
    
    const riskLevelAccess = [...new Set(
      activeSubscriptions.flatMap(sub => 
        Array.from({length: sub.riskLevel || 1}, (_, i) => i + 1)
      )
    )].sort();
    
    const features = [...new Set(
      activeSubscriptions.flatMap(sub => sub.features || [])
    )];
    
    const canViewCharts = riskLevelAccess.some(level => level >= 3);

    return {
      canViewReports: activeSubscriptions.length > 0,
      canViewCharts,
      maxStocksPerQuarter,
      riskLevelAccess,
      features,
      activePlans: activeSubscriptions.map(sub => sub.planName)
    };
  }

  /**
   * Validate user data structure
   * @param {Object} userData - User data to validate
   * @returns {Object} Validation result with isValid flag and message
   */
  validateUserData(userData) {
    const errors = [];

    if (!userData.name?.trim()) {
      errors.push('Name is required');
    }

    if (!userData.email?.trim()) {
      errors.push('Email is required');
    } else if (!isValidEmail(userData.email)) {
      errors.push('Invalid email format');
    }

    if (userData.role && !Object.values(USER_ROLES).includes(userData.role)) {
      errors.push('Invalid role value');
    }

    if (userData.status && !Object.values(USER_STATUSES).includes(userData.status)) { 
      errors.push('Invalid status value');
    }

    return {
      isValid: errors.length === 0,
      errors,
      message: errors.length > 0 ? errors.join(', ') : 'Valid'
    };
  }

  // ===== PRICING PLANS INTEGRATION =====
  
  /**
   * Get available pricing plans for subscriptions
   * @returns {Promise<Object>} Success/error response with plans
   */
  async getAvailablePlans() {
    try {
      const plansCollectionRef = collection(db, this.plansCollection);
      const data = await getDocs(plansCollectionRef);
      const plans = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      
      return createSuccessResponse(plans, 'Available plans fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch available plans', 'PLANS_FETCH_ERROR', error);
    }
  }

  /**
   * Get plan by ID
   * @param {string} planId - Plan document ID
   * @returns {Promise<Object>} Success/error response with plan data
   */
  async getPlanById(planId) {
    try {
      const planRef = doc(db, this.plansCollection, planId);
      const planDoc = await getDoc(planRef);
      
      if (!planDoc.exists()) {
        return createErrorResponse('Plan not found', 'PLAN_NOT_FOUND');
      }

      const plan = { ...planDoc.data(), id: planDoc.id };
      return createSuccessResponse(plan, 'Plan fetched successfully');
    } catch (error) {
      console.log(error);
      return createErrorResponse('Failed to fetch plan', 'PLAN_FETCH_ERROR', error);
    }
  }
  /**
   * Get all pricing plans
   * @returns {Promise<Array>} Array of pricing plans
   */
  async getPricingPlans() {
    try {
      // Use BaseService to get plans from pricingPlans collection
      const plans = await this.getAllFromCollection(this.plansCollection);
      return plans;
    } catch (error) {
      console.error('Error fetching pricing plans:', error);
      throw new Error('Failed to fetch pricing plans');
    }
  }

  // ===== BULK OPERATIONS =====
  
  /**
   * Bulk update user statuses
   * @param {Array} userIds - Array of user IDs
   * @param {string} newStatus - New status for all users
   * @param {string} adminUserId - ID of admin performing bulk update
   * @param {string} reason - Optional reason for bulk update
   * @returns {Promise<Object>} Success/error response with results
   */
  async bulkUpdateUserStatuses(userIds, newStatus, adminUserId = 'admin', reason = '') {
    try {
      if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
        return createErrorResponse('User IDs array is required', 'VALIDATION_ERROR');
      }

      if (!Object.values(USER_STATUSES).includes(newStatus)) {
        return createErrorResponse('Invalid status value', 'VALIDATION_ERROR');
      }

      const results = [];
      const errors = [];

      for (const userId of userIds) {
        try {
          const result = await this.updateUserStatus(userId, newStatus, adminUserId, '', reason);
          if (result.success) {
            results.push({ userId, success: true, data: result.data });
          } else {
            errors.push({ userId, success: false, error: result.message });
          }
        } catch (error) {
          errors.push({ userId, success: false, error: error.message });
        }
      }

      const summary = {
        total: userIds.length,
        successful: results.length,
        failed: errors.length,
        results,
        errors
      };

      if (errors.length === 0) {
        return createSuccessResponse(summary, `All ${userIds.length} users updated successfully`);
      } else if (results.length > 0) {
        return createSuccessResponse(summary, `Partially successful: ${results.length}/${userIds.length} users updated`);
      } else {
        return createErrorResponse('All operations failed', 'BULK_UPDATE_ERROR', summary);
      }
    } catch (error) {
      return createErrorResponse('Failed to perform bulk update', 'BULK_UPDATE_ERROR', error);
    }
  }

  /**
   * Export users data for reporting
   * @param {Object} filters - Filter options for export
   * @returns {Promise<Object>} Success/error response with export data
   */
  async exportUsersData(filters = {}) {
    try {
      const users = await this.getAll();
      
      // Apply filters if provided
      let filteredUsers = users;
      if (filters.status && filters.status !== 'all') {
        filteredUsers = users.filter(user => (user.status || 'pending') === filters.status);
      }
      if (filters.role) {
        filteredUsers = filteredUsers.filter(user => user.role === filters.role);
      }

      // Format data for export
      const exportData = filteredUsers.map(user => ({
        id: user.id,
        name: user.name || '',
        email: user.email || '',
        role: user.role || '',
        status: user.status || 'pending',
        customer_id: user.customer_id || '',
        company: user.company || '',
        designation: user.designation || '',
        phone: user.phone || '',
        created_at: user.createdAt ? user.createdAt.toDate().toISOString() : '',
        updated_at: user.updatedAt ? user.updatedAt.toDate().toISOString() : '',
        last_login: user.lastLoginAt ? user.lastLoginAt.toDate().toISOString() : '',
        active_subscriptions: user.subscriptions?.filter(sub => sub.status === 'active').length || 0,
        total_subscriptions: user.subscriptions?.length || 0,
        permissions: {
          can_view_reports: user.permissions?.canViewReports || false,
          can_view_charts: user.permissions?.canViewCharts || false,
          max_stocks_per_quarter: user.permissions?.maxStocksPerQuarter || 0,
          risk_level_access: user.permissions?.riskLevelAccess?.join(', ') || 'None'
        }
      }));

      return createSuccessResponse(exportData, `Exported ${exportData.length} users successfully`);
    } catch (error) {
      return createErrorResponse('Failed to export users data', 'EXPORT_ERROR', error);
    }
  }
} 